import React, { useState, useEffect } from 'react';
import { OmniDropdownComponent, OmniIconComponent, OmniChipInputComponent, Utils } from '../../../../shared';

const INITIAL_FORM = { clientCodes: [], buyingSystem: '', moAgencyId: '', moOrganizationId: '', market: '' };
const INITIAL_APP_FIELDS = { currency: '', name: '', isApprovalRequired: '', isCurrencyLocal: '', showFlaggedFeature: '', isOptin: '', isActive: '' };
const DROPDOWN_OPTIONS_BUY_SYSTEM = [{ id: 'MediaOcean', value: 'MediaOcean' }, { id: 'MediaOcean-CA', value: 'MediaOcean-CA' }, { id: 'None', value: 'None' }];
const DROPDOWN_OPTIONS_BOOL = [{ id: 'true', value: 'true' }, { id: 'false', value: 'false' }];

const AddNewClientCodeModal = ({ isOpen, onClose, onSave, existingClientCodes = [], isFirstClientCode = false }) => {
    const [formData, setFormData] = useState({ ...INITIAL_FORM, ...(isFirstClientCode ? INITIAL_APP_FIELDS : {}) });
    const [formKey, setFormKey] = useState(0);
    const [duplicateWarning, setDuplicateWarning] = useState('');

    useEffect(() => {
        if (isOpen) {
            setFormData({ ...INITIAL_FORM, clientCodes: [], ...(isFirstClientCode ? INITIAL_APP_FIELDS : {}) });
            setFormKey(prev => prev + 1);
            setDuplicateWarning('');
        }
    }, [isOpen, isFirstClientCode]);

    const handleFieldChange = (field, event) => {
        const dropdownFields = ['buyingSystem', 'isApprovalRequired', 'isCurrencyLocal', 'showFlaggedFeature', 'isOptin', 'isActive'];
        const value = dropdownFields.includes(field) ? Utils.getValue(event) : event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleClientCodesChange = (event) => {
        const chips = event.detail?.value || [];
        const uniqueChips = [...new Set(chips)];
        const existingSet = new Set(existingClientCodes.map(c => c.toLowerCase()));
        const duplicatesInInput = uniqueChips.length < chips.length;
        const existingDuplicates = uniqueChips.filter(chip => existingSet.has(chip.toLowerCase()));
        const filteredChips = uniqueChips.filter(chip => !existingSet.has(chip.toLowerCase()));

        if (existingDuplicates.length > 0) {
            setDuplicateWarning(`Client code(s) already exist: ${existingDuplicates.join(', ')}. Removed.`);
        } else if (duplicatesInInput) {
            setDuplicateWarning('Duplicate client code(s) removed. Only unique values are allowed.');
        } else {
            setDuplicateWarning('');
        }
        setFormData(prev => ({ ...prev, clientCodes: filteredChips }));
    };

    const resetForm = () => {
        setFormData({ ...INITIAL_FORM, clientCodes: [], ...(isFirstClientCode ? INITIAL_APP_FIELDS : {}) });
        setFormKey(prev => prev + 1);
        setDuplicateWarning('');
    };

    const handleSave = () => {
        if (onSave) onSave(formData);
        resetForm();
    };

    const handleClose = () => {
        resetForm();
        if (onClose) onClose();
    };

    const baseFieldsInvalid = formData.clientCodes.length === 0 || !formData.buyingSystem || !formData.moAgencyId.trim() || !formData.moOrganizationId.trim() || !formData.market.trim();
    const appFieldsInvalid = isFirstClientCode && (!formData.currency?.trim() || !formData.name?.trim() || !formData.isApprovalRequired || !formData.isCurrencyLocal || !formData.showFlaggedFeature || !formData.isOptin || !formData.isActive);
    const isSaveDisabled = baseFieldsInvalid || appFieldsInvalid;

    if (!isOpen) return null;

    return (
        <div className='add-client-code-modal-overlay'>
            <div className='add-client-code-modal-dialog'>
                <div className='wizard-header'>
                    <h2>Add New Client Code</h2>
                    <div className='wizard-header-right'>
                        <button className='tertiary' onClick={handleClose} aria-label='Close modal'>
                            <OmniIconComponent icon-id='omni:interactive:close' />
                        </button>
                    </div>
                </div>

                <div className='wizard-content'>
                    <div className='wizard-step'>
                        <div className='form-fields-horizontal-centered'>
                            <div className={`form-group-horizontal${duplicateWarning ? ' has-warning' : ''}`}>
                                <label>* Client Code(s):</label>
                                <OmniChipInputComponent
                                    key={formKey}
                                    label=''
                                    placeholder='Enter client codes'
                                    delimiter=','
                                    value={formData.clientCodes}
                                    onAddChip={handleClientCodesChange}
                                    help-text="Press Enter after typing each client code. You can add multiple client codes separated by commas."
                                    required
                                />
                                {duplicateWarning && (
                                    <span className='warning-text'>
                                        {duplicateWarning}
                                    </span>
                                )}
                            </div>
                            <div className='form-group-horizontal'>
                                <label>* Buying System:</label>
                                <OmniDropdownComponent
                                    hidefooter
                                    placeholder='Select'
                                    options={DROPDOWN_OPTIONS_BUY_SYSTEM}
                                    id='new-client-buyingSystem'
                                    value={[formData.buyingSystem]}
                                    onDropdownChange={(e) => handleFieldChange('buyingSystem', e)}
                                />
                            </div>
                            <div className='form-group-horizontal'>
                                <label>* MO Agency ID:</label>
                                <input
                                    type='text'
                                    className='input'
                                    placeholder='Enter MO agency ID'
                                    value={formData.moAgencyId}
                                    onChange={(e) => handleFieldChange('moAgencyId', e)}
                                />
                            </div>
                            <div className='form-group-horizontal'>
                                <label>* MO Organization ID:</label>
                                <input
                                    type='text'
                                    className='input'
                                    placeholder='Enter MO organization ID'
                                    value={formData.moOrganizationId}
                                    onChange={(e) => handleFieldChange('moOrganizationId', e)}
                                />
                            </div>
                            <div className='form-group-horizontal'>
                                <label>* Market:</label>
                                <input
                                    type='text'
                                    className='input'
                                    placeholder='Enter market'
                                    value={formData.market}
                                    onChange={(e) => handleFieldChange('market', e)}
                                />
                            </div>

                            {isFirstClientCode && (<>
                                <div className='form-group-horizontal'>
                                    <label>* Client Name:</label>
                                    <input
                                        type='text'
                                        className='input'
                                        placeholder='Enter client name'
                                        value={formData.name}
                                        onChange={(e) => handleFieldChange('name', e)}
                                    />
                                </div>
                                <div className='form-group-horizontal'>
                                    <label>* Currency:</label>
                                    <input
                                        type='text'
                                        className='input'
                                        placeholder='Enter currency'
                                        value={formData.currency}
                                        onChange={(e) => handleFieldChange('currency', e)}
                                    />
                                </div>
                                <div className='form-group-horizontal'>
                                    <label>* Is Approval Required:</label>
                                    <OmniDropdownComponent
                                        hidefooter
                                        placeholder='Select'
                                        options={DROPDOWN_OPTIONS_BOOL}
                                        id='new-client-isApprovalRequired'
                                        value={[formData.isApprovalRequired]}
                                        onDropdownChange={(e) => handleFieldChange('isApprovalRequired', e)}
                                    />
                                </div>
                                <div className='form-group-horizontal'>
                                    <label>* Is Currency Local:</label>
                                    <OmniDropdownComponent
                                        hidefooter
                                        placeholder='Select'
                                        options={DROPDOWN_OPTIONS_BOOL}
                                        id='new-client-isCurrencyLocal'
                                        value={[formData.isCurrencyLocal]}
                                        onDropdownChange={(e) => handleFieldChange('isCurrencyLocal', e)}
                                    />
                                </div>
                                <div className='form-group-horizontal'>
                                    <label>* Show Flagged Feature:</label>
                                    <OmniDropdownComponent
                                        hidefooter
                                        placeholder='Select'
                                        options={DROPDOWN_OPTIONS_BOOL}
                                        id='new-client-showFlaggedFeature'
                                        value={[formData.showFlaggedFeature]}
                                        onDropdownChange={(e) => handleFieldChange('showFlaggedFeature', e)}
                                    />
                                </div>
                                <div className='form-group-horizontal'>
                                    <label>* Is Opt-in:</label>
                                    <OmniDropdownComponent
                                        hidefooter
                                        placeholder='Select'
                                        options={DROPDOWN_OPTIONS_BOOL}
                                        id='new-client-isOptin'
                                        value={[formData.isOptin]}
                                        onDropdownChange={(e) => handleFieldChange('isOptin', e)}
                                    />
                                </div>
                                <div className='form-group-horizontal'>
                                    <label>* Is Active:</label>
                                    <OmniDropdownComponent
                                        hidefooter
                                        placeholder='Select'
                                        options={DROPDOWN_OPTIONS_BOOL}
                                        id='new-client-isActive'
                                        value={[formData.isActive]}
                                        onDropdownChange={(e) => handleFieldChange('isActive', e)}
                                    />
                                </div>
                            </>)}
                        </div>
                    </div>
                </div>

                <div className='wizard-footer'>
                    <div className='wizard-actions-right'>
                        <button className='tertiary' onClick={handleClose}>Cancel</button>
                        <button onClick={handleSave} disabled={isSaveDisabled}>Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddNewClientCodeModal;
